// @ts-nocheck
import axios from "axios";
import jwtDecode from "jwt-decode";
import React, { useCallback, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import Breadcrumb from "../../components/breadcrumb/breadcrumb";
import Pagination from "../../components/pagination/Pagination";
import DeleteModalAdmin from "./DeleteModalAdmin";
import CreateAdminModal from "./CreateAdminModal";
import { URL } from "../../service/Service";
import {
  MagnifyingGlassIcon,
  UserPlusIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import ErrorMessage from "../ErrorMessage";
import SuccessMessage from "../SuccessMessage";
import { useTranslation } from "../../i18n/LanguageContext";

export const TableAdmin = () => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [originalPeople, setOriginalPeople] = useState([]);
  const [peopleData, setPeopleData] = useState(originalPeople);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const [userRole, setUserRole] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [placeholderText, setPlaceholderText] = useState(
      t("admins.selectRole")
  );

  const pages = [
    {
      name: t("admins.breadcrumb"),
      href: "/admin/admins",
      current: true,
    },
  ];

  const jwtToken = sessionStorage.getItem("jwt");

  const loggedUserEmail = (() => {
    if (!jwtToken) return "";

    try {
      const decodedToken = jwtDecode(jwtToken);
      return String(decodedToken?.sub || "").toLowerCase();
    } catch (error) {
      console.error("Unable to decode authenticated user token:", error);
      return "";
    }
  })();

  const refreshAdmins = useCallback(async () => {
    const jwtToken = sessionStorage.getItem("jwt");

    try {
      const res = await axios.get(`${URL}/api/findAll/admin/manager`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      setOriginalPeople(res.data);
      setPeopleData(res.data);
      setErrorMessage("");
    } catch (error) {
      console.error(error);
      setErrorMessage(t("admins.loadError"));
    }
  }, [t]);

  useEffect(() => {
    const storedUserRole = sessionStorage.getItem("userRole");

    if (storedUserRole) {
      setUserRole(storedUserRole.toUpperCase());
    } else {
      setUserRole("MANAGER");
      console.warn("User role not found, defaulting to MANAGER.");
    }

    refreshAdmins();
  }, [refreshAdmins]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 500) {
        setPlaceholderText(t("admins.selectRoleShort"));
      } else if (window.innerWidth < 700) {
        setPlaceholderText(t("admins.selectRoleMedium"));
      } else {
        setPlaceholderText(t("admins.selectRole"));
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [t]);

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = peopleData.slice(
      indexOfFirstPost,
      indexOfLastPost
  );

  const handleChange = (event) => {
    const newSearchTerm = event.target.value.toLowerCase();

    setSearchTerm(newSearchTerm);
    applyFilters(newSearchTerm, roleFilter);
  };

  const handleRoleFilterChange = (event) => {
    const selectedRole = event.target.value;

    setRoleFilter(selectedRole);
    applyFilters(searchTerm, selectedRole);
  };

  const applyFilters = (searchValue, selectedRole) => {
    let filteredData = originalPeople;

    if (searchValue) {
      filteredData = filteredData.filter(
          (data) =>
              data.name.toLowerCase().includes(searchValue) ||
              data.email.toLowerCase().includes(searchValue)
      );
    }

    if (selectedRole) {
      filteredData = filteredData.filter(
          (data) => data.role === selectedRole
      );
    }

    setPeopleData(filteredData);
    setCurrentPage(1);
  };

  /**
   * Elimina o utilizador no backend e, em caso de sucesso,
   * atualiza imediatamente a tabela sem necessitar de refresh/F5.
   */
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${URL}/api/delete/user/${id}`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      /*
       * Atualiza a lista original.
       * Isto é importante porque é esta lista que serve de base
       * às pesquisas e filtros seguintes.
       */
      setOriginalPeople((previousPeople) =>
          previousPeople.filter((person) => person.id !== id)
      );

      /*
       * Atualiza a lista atualmente visível.
       * Desta forma a linha desaparece imediatamente da tabela.
       */
      setPeopleData((previousPeople) => {
        const updatedPeople = previousPeople.filter(
            (person) => person.id !== id
        );

        /*
         * Se eliminarmos o último registo de uma página,
         * recuamos automaticamente para uma página válida.
         */
        const totalPages = Math.max(
            1,
            Math.ceil(updatedPeople.length / postsPerPage)
        );

        setCurrentPage((previousPage) =>
            Math.min(previousPage, totalPages)
        );

        return updatedPeople;
      });

      setErrorMessage("");

      return { success: true };
    } catch (error) {
      console.error("Error deleting user:", error);

      setSuccessMessage("");

      const responseData = error?.response?.data;
      const message =
          (typeof responseData === "string"
              ? responseData
              : responseData?.message) ||
          t("admins.deleteError");

      setErrorMessage(message);

      return { success: false, message };
    }
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleModalOpen = () => {
    console.log("Opening modal with userRole:", userRole);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleRoleChange = async (event, email) => {
    const newRole = event.target.value;

    try {
      const response = await axios.patch(
          `${URL}/api/admin/change/user/role`,
          null,
          {
            params: {
              emailString: email,
              roleString: newRole,
            },
            headers: {
              Authorization: `Bearer ${jwtToken}`,
            },
          }
      );

      if (response.status === 200) {
        setErrorMessage("");
        setSuccessMessage(t("admins.roleUpdated"));

        refreshAdmins();
      } else {
        setSuccessMessage("");
        setErrorMessage(t("admins.roleUpdateError"));
      }
    } catch (error) {
      console.error("Error updating role:", error);

      setSuccessMessage("");

      setErrorMessage(
          error?.response?.data?.message ||
          t("admins.roleUpdateError")
      );
    }
  };

  return (
      <Container fluid className="portal-page">
        <ErrorMessage message={errorMessage} />
        <SuccessMessage message={successMessage} />

        <Breadcrumb pages={pages} />

        <header className="portal-page-header">
          <div className="portal-page-heading">
          <span className="portal-eyebrow">
            Backoffice Eupago
          </span>

            <h1>{t("admins.title")}</h1>

            <p>{t("admins.description")}</p>
          </div>

          {userRole === "ADMIN" && (
              <button
                  type="button"
                  className="portal-button portal-button-primary"
                  onClick={handleModalOpen}
              >
                <UserPlusIcon />

                {t("admins.new")}
              </button>
          )}
        </header>

        <section className="portal-panel">
          <div className="portal-toolbar">
            <label className="portal-search">
            <span className="sr-only">
              {t("admins.search")}
            </span>

              <MagnifyingGlassIcon aria-hidden="true" />

              <input
                  type="search"
                  placeholder={t("admins.search")}
                  onChange={handleChange}
                  value={searchTerm}
              />
            </label>

            <div className="portal-select">
              <select
                  value={roleFilter}
                  onChange={handleRoleFilterChange}
                  aria-label={t("admins.selectRole")}
                  style={{
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    backgroundImage: "none",
                  }}
                  className="portal-select-control"
              >
                <option value="" disabled hidden>
                  {placeholderText}
                </option>

                <option value="">
                  {t("admins.all")}
                </option>

                <option value="ADMIN">
                  {t("role.admin")}
                </option>

                <option value="MANAGER">
                  {t("role.manager")}
                </option>
              </select>

              <span className="portal-select-icon">
              <svg
                  viewBox="0 0 12 8"
                  className="h-2 w-3 text-gray-500"
                  aria-hidden="true"
              >
                <path
                    d="M1 1.25 6 6.75 11 1.25Z"
                    fill="currentColor"
                />
              </svg>
            </span>
            </div>

            <span className="portal-result-count">
            {peopleData.length} resultado(s)
          </span>
          </div>

          <div className="portal-table-wrap">
            <table
                className="portal-table"
                style={{ tableLayout: "fixed" }}
            >
              <thead>
              <tr>
                {userRole === "ADMIN" && (
                    <th
                        className="portal-action-column"
                        style={{ width: "10%" }}
                    >
                    <span className="sr-only">
                      {t("common.delete")}
                    </span>
                    </th>
                )}

                <th style={{ width: "35%" }}>
                  {t("settings.name")}
                </th>

                <th style={{ width: "35%" }}>
                  Email
                </th>

                <th style={{ width: "20%" }}>
                  {t("admins.accountType")}
                </th>
              </tr>
              </thead>

              <tbody>
              {currentPosts.map((person) => (
                  <tr key={person.id}>
                    {userRole === "ADMIN" && (
                        <td className="portal-action-column">
                          <DeleteModalAdmin
                              userRole={userRole}
                              targetRole={person.role}
                              userId={person.id}
                              handleDelete={handleDelete}
                              disabled={
                                Boolean(loggedUserEmail) &&
                                person.email?.toLowerCase() === loggedUserEmail
                              }
                              disabledReason="Não pode eliminar a sua própria conta."
                          />
                        </td>
                    )}

                    <td className="portal-cell-primary">
                      {person.name}
                    </td>

                    <td className="portal-cell-muted">
                      {person.email}
                    </td>

                    <td>
                      {userRole === "ADMIN" ? (
                          <select
                              value={person.role}
                              onChange={(event) =>
                                  handleRoleChange(
                                      event,
                                      person.email
                                  )
                              }
                              style={{
                                border: "none",
                                padding: 0,
                                fontSize: "inherit",
                                lineHeight: "inherit",
                                cursor: "pointer",
                                boxShadow: "none",
                                paddingRight: "40px",
                              }}
                              className="portal-inline-select"
                          >
                            <option value="ADMIN">
                              {t("role.admin")}
                            </option>

                            <option value="MANAGER">
                              {t("role.manager")}
                            </option>
                          </select>
                      ) : person.role === "ADMIN" ? (
                          t("role.admin")
                      ) : (
                          t("role.manager")
                      )}
                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
          </div>

          {currentPosts.length === 0 && (
              <div className="portal-empty-state">
                <UsersIcon />

                <p>{t("admins.loadError")}</p>
              </div>
          )}

          <footer className="portal-panel-footer">
            <Pagination
                postsPerPage={postsPerPage}
                totalPosts={peopleData.length}
                paginate={paginate}
                currentPage={currentPage}
            />
          </footer>
        </section>

        <CreateAdminModal
            show={isModalOpen}
            handleClose={handleModalClose}
            userRole={userRole}
            refreshAdmins={refreshAdmins}
        />
      </Container>
  );
};

export default TableAdmin;